import React, { useState, useEffect, useRef } from 'react';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import jsPDF from 'jspdf';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';

// Stripe redirects back with session_id either in the real query string or in
// the hash-route query (http://host/#/success?session_id=cs_xxx)
export const getSessionIdFromLocation = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const searchSessionId = searchParams.get('session_id');
  if (searchSessionId) {
    return searchSessionId;
  }
  const hash = window.location.hash || '';
  const queryString = hash.split('?')[1];
  if (!queryString) {
    return null;
  }
  const hashParams = new URLSearchParams(queryString);
  return hashParams.get('session_id');
};

const PaymentSuccess = ({
  onNavigate,
  tokenTimeoutMs = null,
  showLoginCta = false,
  autoLoginOnTimeout = false
}) => {
  const { verifyPayment, token, login } = useAuth();
  const { t } = useTranslation();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');
  const [orderType, setOrderType] = useState(null); // 'credits', 'pdf', 'print'
  const [creditsRemaining, setCreditsRemaining] = useState(null); // Store credits from API response
  const [showLogin, setShowLogin] = useState(false);
  const hasVerifiedRef = useRef(false);
  const reauthKeyRef = useRef(null);

  // After a Stripe redirect on mobile Safari the Auth0 session may not restore
  // in time. Optionally retry a silent login once, then surface a login CTA.
  useEffect(() => {
    if (!tokenTimeoutMs || token) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      if (!hasVerifiedRef.current && !token) {
        if (autoLoginOnTimeout) {
          const reauthKey = reauthKeyRef.current || `paymentReauthAttempted:${getSessionIdFromLocation() || 'unknown'}`;
          reauthKeyRef.current = reauthKey;
          const alreadyAttempted = sessionStorage.getItem(reauthKey) === '1';
          if (!alreadyAttempted) {
            sessionStorage.setItem(reauthKey, '1');
            setMessage(t('payment.refreshingSession'));
            login();
            return;
          }
        }

        setStatus('error');
        setMessage(t('payment.signInAgain'));
        setShowLogin(true);
      }
    }, tokenTimeoutMs);

    return () => clearTimeout(timeoutId);
  }, [token, tokenTimeoutMs, autoLoginOnTimeout, login, t]);

  const generateAndDownloadPDF = async (boardId) => {
    try {
      console.log('generateAndDownloadPDF called with boardId:', boardId);
      setMessage(t('payment.generatingPdf'));

      // Fetch board data
      const boardData = await api.getBoard(token, boardId);
      const dataString = boardData.board?.data || boardData.data;
      if (!dataString) throw new Error('No board data found in response');
      const parsedData = JSON.parse(dataString);

      const canvasImages = parsedData.canvasImages || [];
      const canvasElements = parsedData.canvasElements || [];
      const background = parsedData.background || { type: 'gradient', value: 'from-gray-50 to-gray-100' };
      const canvasFrame = parsedData.canvasFrame;
      const canvasSize = parsedData.canvasSize || { width: 210, height: 297 };
      const canvasWidth = parsedData.canvasWidth || 800;
      const canvasHeight = canvasWidth * (canvasSize.height / canvasSize.width);

      console.log('Canvas size:', canvasWidth, 'x', canvasHeight);

      // Create canvas using Canvas API
      const pdfCanvas = document.createElement('canvas');
      const scale = 3; // High quality
      pdfCanvas.width = canvasWidth * scale;
      pdfCanvas.height = canvasHeight * scale;
      const ctx = pdfCanvas.getContext('2d');
      ctx.scale(scale, scale);

      // 1. Draw background
      if (background.type === 'solid') {
        ctx.fillStyle = background.value;
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      } else if (background.type === 'gradient') {
        const parts = background.value.split(' ');
        const from = parts[0].replace('from-', '');
        const to = parts[1].replace('to-', '');
        const colorMap = { 'gray-50': '#F9FAFB', 'gray-100': '#F3F4F6', 'purple-50': '#FAF5FF', 'pink-50': '#FDF2F8', 'orange-50': '#FFF7ED' };
        const gradient = ctx.createLinearGradient(0, 0, canvasWidth, canvasHeight);
        gradient.addColorStop(0, colorMap[from] || '#ffffff');
        gradient.addColorStop(1, colorMap[to] || '#ffffff');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      } else if (background.type === 'pattern') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);
        ctx.fillStyle = '#ddd';
        if (background.value === 'dots') {
          for (let x = 0; x < canvasWidth; x += 20) {
            for (let y = 0; y < canvasHeight; y += 20) {
              ctx.beginPath();
              ctx.arc(x, y, 1, 0, 2 * Math.PI);
              ctx.fill();
            }
          }
        } else if (background.value === 'grid') {
          for (let x = 0; x < canvasWidth; x += 20) ctx.fillRect(x, 0, 1, canvasHeight);
          for (let y = 0; y < canvasHeight; y += 20) ctx.fillRect(0, y, canvasWidth, 1);
        }
      } else {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      }

      // 2. Load images and combine all elements
      const imageLoadingPromises = canvasImages.map(img =>
        new Promise((resolve, reject) => {
          const image = new Image();
          image.crossOrigin = 'anonymous';
          image.onload = () => resolve({ ...img, type: 'image', imageElement: image });
          image.onerror = reject;
          image.src = img.imageUrl;
        })
      );
      const loadedImages = await Promise.all(imageLoadingPromises);
      const allElements = [...loadedImages, ...canvasElements];

      // 3. Sort all elements by z-index
      const sortedElements = allElements.sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

      // 4. Draw all elements
      sortedElements.forEach(el => {
        ctx.save();
        if (el.type === 'image') {
          const targetWidth = el.width, targetHeight = el.height;
          const imgAR = el.imageElement.naturalWidth / el.imageElement.naturalHeight;
          const targetAR = targetWidth / targetHeight;
          let sx = 0, sy = 0, sw = el.imageElement.naturalWidth, sh = el.imageElement.naturalHeight;

          if (imgAR > targetAR) {
            sw = el.imageElement.naturalHeight * targetAR;
            sx = (el.imageElement.naturalWidth - sw) / 2;
          } else {
            sh = el.imageElement.naturalWidth / targetAR;
            sy = (el.imageElement.naturalHeight - sh) / 2;
          }
          ctx.translate(el.x + targetWidth / 2, el.y + targetHeight / 2);
          ctx.rotate((el.rotation * Math.PI) / 180);
          ctx.drawImage(el.imageElement, sx, sy, sw, sh, -targetWidth / 2, -targetHeight / 2, targetWidth, targetHeight);
        } else {
          ctx.translate(el.x, el.y);
          ctx.rotate((el.rotation * Math.PI) / 180);
          if (el.type === 'emoji') {
            ctx.font = `${el.size || 48}px Arial`;
            ctx.fillText(el.content, 0, (el.size || 48) * 0.8);
          } else if (el.type === 'text') {
            const fontSize = el.fontSize || 48;
            ctx.font = `${el.fontWeight || 'normal'} ${el.fontStyle || 'normal'} ${fontSize}px Arial`;
            ctx.fillStyle = el.color || '#000000';

            // Handle multi-line text (split by newlines)
            const lines = (el.content || '').split('\n');
            const lineHeight = fontSize * 1.2; // Standard line height

            lines.forEach((line, index) => {
              ctx.fillText(line, 0, fontSize * 0.8 + (index * lineHeight));
            });
          }
        }
        ctx.restore();
      });

      // 5. Draw frame on top
      if (canvasFrame && canvasFrame.border) {
        // Parse border string like "4px solid #8B4513"
        const borderMatch = canvasFrame.border.match(/(\d+)px\s+(\w+)\s+(.+)/);
        if (borderMatch) {
          const borderWidth = parseInt(borderMatch[1]);
          const borderStyle = borderMatch[2]; // 'solid', 'double', etc.
          const borderColor = borderMatch[3];

          ctx.lineWidth = borderWidth;
          ctx.strokeStyle = borderColor;

          // For double borders, draw two rectangles
          if (borderStyle === 'double') {
            const outerInset = borderWidth / 4;
            const innerInset = borderWidth * 3 / 4;
            ctx.strokeRect(outerInset, outerInset, canvasWidth - (outerInset * 2), canvasHeight - (outerInset * 2));
            ctx.strokeRect(innerInset, innerInset, canvasWidth - (innerInset * 2), canvasHeight - (innerInset * 2));
          } else {
            // Inset the frame by half its width to keep it inside canvas
            const inset = borderWidth / 2;
            ctx.strokeRect(inset, inset, canvasWidth - (inset * 2), canvasHeight - (inset * 2));
          }

          // Handle Polaroid style with extra bottom border
          if (canvasFrame.bottomBorder) {
            const bottomBorder = parseInt(canvasFrame.bottomBorder);
            ctx.fillStyle = borderColor;
            ctx.fillRect(0, canvasHeight - bottomBorder, canvasWidth, bottomBorder);
          }
        }
      }

      // 6. Create and save PDF
      const pdf = new jsPDF({
        orientation: canvasSize.width > canvasSize.height ? 'landscape' : 'portrait',
        unit: 'mm',
        format: [canvasSize.width, canvasSize.height]
      });
      const imgData = pdfCanvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, 0, canvasSize.width, canvasSize.height);
      pdf.save(`vision-board-${boardId}.pdf`);

      setMessage(t('payment.pdfDownloaded'));
      setTimeout(() => onNavigate('/'), 2000);
    } catch (error) {
      console.error('PDF generation error:', error);
      setMessage(t('payment.pdfGenerationFailed'));
      setTimeout(() => onNavigate('/'), 2000);
    }
  };

  useEffect(() => {
    const verifySession = async () => {
      // Prevent double execution in React strict mode
      if (hasVerifiedRef.current) {
        console.log('Already verified, skipping...');
        return;
      }

      // Wait for token to be available (Auth0 may still be loading after redirect)
      if (!token) {
        console.log('Waiting for authentication token...');
        return; // Effect will re-run when token changes
      }

      hasVerifiedRef.current = true;

      const sessionId = getSessionIdFromLocation();

      if (!sessionId) {
        setStatus('error');
        setMessage(t('payment.noSessionFound'));
        return;
      }

      const result = await verifyPayment(sessionId);
      console.log('Payment verification result:', result);

      if (result.success) {
        setStatus('success');
        console.log('Result data:', result.data);
        if (reauthKeyRef.current) {
          sessionStorage.removeItem(reauthKeyRef.current);
        }

        if (result.data.credits_added) {
          setOrderType('credits');
          setMessage(t('payment.creditsAdded', { count: result.data.credits_added }));
          // Use credits from API response directly to avoid stale state
          if (result.data.usage?.remaining !== undefined) {
            setCreditsRemaining(result.data.usage.remaining);
          }
        } else if (result.data.order_type) {
          setOrderType(result.data.order_type);
          console.log('Order type:', result.data.order_type, 'Board ID:', result.data.board_id);

          if (result.data.order_type === 'pdf') {
            const boardId = result.data.board_id;
            if (boardId) {
              console.log('Starting PDF generation for board:', boardId);
              await generateAndDownloadPDF(boardId);
            } else {
              console.error('No board_id in result.data');
              setMessage(t('payment.pdfOrderProcessed'));
              setTimeout(() => {
                onNavigate('/');
              }, 3000);
            }
          } else if (result.data.order_type === 'print') {
            setMessage(t('payment.printOrderPlaced'));
            setTimeout(() => {
              onNavigate('/');
            }, 3000);
          }
        } else {
          console.log('No credits_added or order_type in result.data');
          setMessage(t('payment.paymentProcessed'));
        }
      } else {
        setStatus('error');
        setMessage(result.error || t('payment.verificationFailed'));
      }
    };

    verifySession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyPayment, onNavigate, token, t]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {status === 'verifying' && (
          <>
            <Loader2 className="w-16 h-16 animate-spin text-purple-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('payment.verifyingPayment')}</h2>
            <p className="text-gray-600">{t('payment.pleaseWait')}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('payment.paymentSuccessful')}</h2>
            <p className="text-gray-600 mb-6">{message}</p>

            {/* Only show credits for credit purchases */}
            {orderType === 'credits' && creditsRemaining !== null && (
              <div className="bg-purple-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-600 mb-1">{t('payment.yourCurrentCredits')}</p>
                <p className="text-3xl font-bold text-purple-600">{creditsRemaining}</p>
              </div>
            )}

            {/* Auto-redirect message for PDF/Print */}
            {(orderType === 'pdf' || orderType === 'print') && (
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-600">{t('payment.redirecting')}</p>
              </div>
            )}

            {/* Only show button for credit purchases */}
            {orderType === 'credits' && (
              <button
                onClick={() => onNavigate('/')}
                className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition font-medium"
              >
                {t('payment.backToDashboard')}
              </button>
            )}
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('payment.verificationFailed')}</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            {showLogin && showLoginCta && (
              <button
                onClick={login}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition mb-3"
              >
                {t('auth.loginToVerify')}
              </button>
            )}
            <button
              onClick={() => onNavigate('/')}
              className="w-full bg-gray-600 text-white py-3 rounded-lg font-medium hover:bg-gray-700 transition"
            >
              {t('payment.backToDashboard')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export const PaymentSuccessWeb = ({ onNavigate }) => (
  <PaymentSuccess onNavigate={onNavigate} />
);

export const PaymentSuccessMobile = ({ onNavigate }) => (
  <PaymentSuccess
    onNavigate={onNavigate}
    tokenTimeoutMs={8000}
    showLoginCta
    autoLoginOnTimeout
  />
);

export const PaymentSuccessIOS = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [deeplink, setDeeplink] = useState('');
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    const sessionIdFromLocation = getSessionIdFromLocation();
    setSessionId(sessionIdFromLocation);

    const nextLink = sessionIdFromLocation
      ? `mymanifest://success?session_id=${encodeURIComponent(sessionIdFromLocation)}`
      : 'mymanifest://success';

    setDeeplink(nextLink);
    window.location.href = nextLink;
  }, []);

  const handleContinueWeb = () => {
    if (sessionId) {
      onNavigate(`/success-web?session_id=${encodeURIComponent(sessionId)}`);
    } else {
      onNavigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <Loader2 className="w-16 h-16 animate-spin text-purple-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('appRedirect.returning')}</h2>
        <p className="text-gray-600 mb-6">
          {t('appRedirect.autoOpenHint')}
        </p>
        {deeplink && (
          <a
            href={deeplink}
            className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition font-medium inline-flex items-center justify-center mb-3"
          >
            {t('appRedirect.openApp')}
          </a>
        )}
        <button
          onClick={handleContinueWeb}
          className="w-full text-sm text-gray-500 hover:text-gray-700"
        >
          {t('appRedirect.continueOnWeb')}
        </button>
      </div>
    </div>
  );
};

export default PaymentSuccess;
